import React, { useState } from 'react';
import { Menu, X, Compass, Headphones, Box, BookOpen, MapPin } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { CASA_CARTARUM_LOGO } from '../data/mockMaps';

const Navbar = ({ onOpenExplorer, onOpenAudio }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    if (location.pathname !== '/') {
      window.location.href = '/';
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        backgroundColor: '#2C1810',
        borderBottom: '1px solid #C5A059',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 max-[399px]:px-2 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Brand - Logo Static */}
          <div 
            className="flex items-center gap-2 cursor-pointer group flex-shrink-0" 
            onClick={scrollToTop}
            data-testid="navbar-brand"
          >
            <img 
              src={CASA_CARTARUM_LOGO} 
              alt="Casa Cartarum"
              width={160}
              height={160}
              className="transition-opacity duration-300 group-hover:opacity-90"
              style={{
                height: '80px',
                width: 'auto',
                objectFit: 'contain',
                imageRendering: 'auto',
                filter: 'contrast(1.05) saturate(1.02)',
                transform: 'translateZ(0) scale(1)',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
            />
          </div>

          {/* Text "Casa Cartarum" - CENTRAT pe mobil */}
          <div className="md:hidden flex-1 flex justify-center">
            <div className="flex flex-col items-center">
              <span 
                className="text-lg min-[400px]:text-sm tracking-widest"
                style={{ 
                  fontFamily: "'Cinzel', serif",
                  color: '#EFEBE9',
                  fontWeight: 500,
                  letterSpacing: '0.1em'
                }}
              >
                Casa Cartarum
              </span>
              <span 
                className="text-xs min-[400px]:text-[9px] tracking-wide"
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  color: '#C5A059',
                  fontStyle: 'italic'
                }}
              >
                Colecție Istorică
              </span>
            </div>
          </div>

          {/* Desktop: Text lângă logo */}
          <div className="hidden md:flex flex-col ml-4">
            <span 
              className="text-base tracking-widest"
              style={{ 
                fontFamily: "'Cinzel', 'Playfair Display', Georgia, serif",
                color: '#EFEBE9',
                fontWeight: 500,
                letterSpacing: '0.1em'
              }}
            >
              Casa Cartarum
            </span>
            <span 
              className="text-xs tracking-wide"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: '#C5A059',
                fontStyle: 'italic'
              }}
            >
              Colecție Istorică
            </span>
          </div>

          {/* Spacer pentru desktop */}
          <div className="hidden md:flex flex-1"></div>

          {/* Desktop Menu */}
          <ul className="hidden md:flex items-center gap-1">
            <li>
              <button
                onClick={onOpenExplorer}
                className="nav-link-antique"
                data-testid="navbar-explorer-btn"
              >
                <Compass size={16} className="nav-icon" />
                <span>Explorer</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => scrollToSection('galerie')}
                className="nav-link-antique"
                data-testid="navbar-galerie-btn"
              >
                <span>Galerie</span>
              </button>
            </li>
            <li>
              <button
                onClick={onOpenAudio}
                className="nav-link-antique"
                data-testid="navbar-audio-btn"
              >
                <Headphones size={16} className="nav-icon" />
                <span>Audio</span>
              </button>
            </li>
            <li>
              <Link
                to="/texte-antice"
                className="nav-link-antique"
                data-testid="navbar-texte-antice-btn"
              >
                <BookOpen size={16} className="nav-icon" />
                <span>Cronici Istorice</span>
              </Link>
            </li>
          </ul>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded transition-colors duration-300 min-[400px]:ml-4"
            style={{ color: '#EFEBE9' }}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{
          backgroundColor: '#2C1810',
          borderTop: '1px solid rgba(197, 160, 89, 0.3)'
        }}
      >
        <ul className="px-4 py-4 space-y-1">
          <li>
            <Link
              to="/galerie-3d"
              onClick={() => setIsMenuOpen(false)}
              className="mobile-nav-link"
            >
              <Box size={18} />
              <span>Galerie 3D</span>
            </Link>
          </li>
          <li>
            <button
              onClick={() => scrollToSection('galerie')}
              className="mobile-nav-link w-full text-left"
            >
              <MapPin size={18} />
              <span>Galerie Hărți</span>
            </button>
          </li>
          <li>
            <button 
              onClick={() => { onOpenAudio?.(); setIsMenuOpen(false); }}
              className="mobile-nav-link w-full text-left"
            >
              <Headphones size={18} />
              <span>Rezumate Audio</span>
            </button>
          </li>
          <li>
            <Link 
              to="/texte-antice"
              onClick={() => setIsMenuOpen(false)}
              className="mobile-nav-link"
              data-testid="mobile-texte-antice-btn"
            >
              <BookOpen size={18} />
              <span>Cronici Istorice</span>
            </Link>
          </li>
        </ul>
      </div>

      <style>{`
        .nav-link-antique {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          font-family: 'Cinzel', 'Playfair Display', Georgia, serif;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.1em;
          color: #EFEBE9;
          text-transform: uppercase;
          background: transparent;
          border: none;
          cursor: pointer;
          position: relative;
          transition: all 0.3s ease;
        }

        .nav-link-antique::after {
          content: '';
          position: absolute;
          bottom: 6px;
          left: 20px;
          right: 20px;
          height: 1px;
          background: #C5A059;
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }

        .nav-link-antique:hover {
          color: #C5A059;
        }

        .nav-link-antique:hover::after {
          transform: scaleX(1);
        }

        .nav-link-antique .nav-icon {
          opacity: 0.8;
          transition: opacity 0.3s ease;
        }

        .nav-link-antique:hover .nav-icon {
          opacity: 1;
        }

        .mobile-nav-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          font-family: 'Cinzel', 'Playfair Display', Georgia, serif;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.08em;
          color: #EFEBE9;
          text-decoration: none;
          border-radius: 4px;
          transition: all 0.3s ease;
          border-left: 2px solid transparent;
        }

        .mobile-nav-link:hover {
          color: #C5A059;
          background: rgba(197, 160, 89, 0.1);
          border-left-color: #C5A059;
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
